import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, ArrowRight, RefreshCw, Loader2, Users, Package, Tag, Sparkles,
  Zap, Target, ShieldCheck, CheckCircle, AlertTriangle,
  ChevronDown, ChevronUp, ChevronRight, Wand2, Link2,
  Heart, Trash2, Bookmark, Plus, Star,
} from "lucide-react";
import type { Persona, Product, Offer, NarrativeGeneration, NarrativeAngle, SavedOpportunity } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { GenerationStatusBadge, FAMILY_COLORS, NarrativeAngleCard } from "./narratives";
import { GenerationWizard } from "@/components/generation-wizard";
import { NarrativeFunnelStageBadge } from "@/components/narrative-funnel-stage-badge";
import { optimizedImg } from "@/lib/img";
import { createNarrativeAngleQueue } from "@/lib/narrative-angle-queue";

type AnalyzerOutput = {
  persona_core?: Record<string, any>;
  product_core?: Record<string, any>;
  intersections?: Array<{
    persona_signal: string;
    product_signal: string;
    strength: number;
    type: string;
    why_it_matters: string;
  }>;
  top_opportunities?: Array<{
    title: string;
    opportunity_type: string;
    core_tension: string;
    persona_reason: string;
    product_anchor: string;
    why_promising: string;
    strength: number;
  }>;
  readiness_assessment?: {
    overall_score: number;
    ready_for_generation: boolean;
    strengths?: string[];
    warnings?: string[];
  };
};

type OpportunityItem = NonNullable<AnalyzerOutput["top_opportunities"]>[0];

type AngleGroupingEntry = {
  source_opportunity_title: string;
  source_opportunity_type: string;
  source_opportunity_id: string;
  angle_names: string[];
};

type GenerationWithData = NarrativeGeneration & {
  angles: NarrativeAngle[];
  parsedOutputJson?: AnalyzerOutput | null;
  angleGroupingJson?: AngleGroupingEntry[] | null;
};

const OPPORTUNITY_FAMILY_MAP: Record<string, string> = {
  conflict: "Conflict",
  identity: "Identity",
  outcome: "Outcome",
  trust: "Trust",
  life_fit: "Life-Fit",
  worldview: "Worldview",
};

function angleAvgScore(a: NarrativeAngle): number {
  const scores = [a.resonanceScore, a.proofScore, a.distinctivenessScore, a.emotionalScore].filter((s): s is number => s !== null);
  return scores.length > 0 ? scores.reduce((x, y) => x + y, 0) / scores.length : 0;
}

function sortAnglesByScore(angles: NarrativeAngle[]): NarrativeAngle[] {
  return [...angles].sort((a, b) => angleAvgScore(b) - angleAvgScore(a));
}

function StrengthDots({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 w-1.5 rounded-full ${i < value ? "bg-primary" : "bg-muted"}`}
        />
      ))}
      <span className="text-[10px] font-semibold ml-1.5">{value}/10</span>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, count, action }: { icon: any; title: string; count?: number; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
        <Icon className="h-4 w-4 text-primary" />
        {title}
        {count !== undefined && (
          <Badge variant="secondary" className="text-[10px] rounded-full px-2">{count}</Badge>
        )}
      </h2>
      {action}
    </div>
  );
}

function formatFieldLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

const PERSONA_KEYS: Array<{ key: string; label: string; style: "text" | "list" | "tags" }> = [
  { key: "worldview", label: "Worldview", style: "text" },
  { key: "primary_pain", label: "Primary Pain", style: "text" },
  { key: "desired_outcome", label: "Desired Outcome", style: "text" },
  { key: "deep_fear", label: "Deep Fear", style: "text" },
  { key: "fears", label: "Fears", style: "list" },
  { key: "frustrations", label: "Frustrations", style: "list" },
  { key: "desires", label: "Desires", style: "list" },
  { key: "beliefs", label: "Core Beliefs", style: "list" },
  { key: "core_beliefs", label: "Core Beliefs", style: "list" },
  { key: "emotional_drivers", label: "Emotional Drivers", style: "list" },
  { key: "buying_triggers", label: "Buying Triggers", style: "list" },
  { key: "trust_requirements", label: "Trust Requirements", style: "list" },
  { key: "identity_signals", label: "Identity Signals", style: "tags" },
  { key: "enemies", label: "Enemies", style: "tags" },
];

const PRODUCT_KEYS: Array<{ key: string; label: string; style: "text" | "list" | "tags" }> = [
  { key: "category", label: "Category", style: "text" },
  { key: "job_to_be_done", label: "Job To Be Done", style: "text" },
  { key: "key_differentiator", label: "Key Differentiator", style: "text" },
  { key: "proof_points", label: "Proof Points", style: "tags" },
  { key: "mechanisms", label: "Mechanisms", style: "list" },
  { key: "core_benefits", label: "Core Benefits", style: "list" },
  { key: "worldview_signals", label: "Worldview Signals", style: "tags" },
  { key: "use_moments", label: "Use Moments", style: "tags" },
  { key: "objection_surfaces", label: "Objection Surfaces", style: "list" },
  { key: "differentiators", label: "Differentiators", style: "list" },
];

function CoreSection({ data, icon: Icon, title }: { data: Record<string, any> | undefined; icon: any; title: string }) {
  if (!data || Object.keys(data).length === 0) return null;
  const [expanded, setExpanded] = useState(false);

  const isPersona = title.toLowerCase().includes("persona");
  const fieldDefs = isPersona ? PERSONA_KEYS : PRODUCT_KEYS;
  const accentText = isPersona ? "text-violet-600 dark:text-violet-400" : "text-emerald-600 dark:text-emerald-400";
  const accentBg = isPersona ? "bg-violet-500/8" : "bg-emerald-500/8";
  const subtleText = isPersona ? "text-violet-500/70 dark:text-violet-400/70" : "text-emerald-500/70 dark:text-emerald-400/70";

  const summary = data.summary || data.name || null;

  type FieldEntry = { label: string; style: "text" | "list" | "tags"; value?: string; items?: string[] };
  const allFields: FieldEntry[] = [];
  const usedKeys = new Set(["summary", "name"]);

  for (const def of fieldDefs) {
    usedKeys.add(def.key);
    const val = data[def.key];
    if (!val) continue;
    if (def.style === "text" && typeof val === "string") {
      allFields.push({ label: def.label, style: "text", value: val });
    } else if (Array.isArray(val) && val.length > 0) {
      allFields.push({
        label: def.label,
        style: def.style,
        items: val.map((v: any) => typeof v === "string" ? v : JSON.stringify(v)),
      });
    }
  }

  for (const [key, val] of Object.entries(data)) {
    if (usedKeys.has(key)) continue;
    if (typeof val === "string" && val.length > 0) {
      allFields.push({ label: formatFieldLabel(key), style: "text", value: val });
    } else if (Array.isArray(val) && val.length > 0) {
      allFields.push({
        label: formatFieldLabel(key),
        style: "list",
        items: val.map((v: any) => typeof v === "string" ? v : JSON.stringify(v)),
      });
    }
  }

  const gradientBorder = isPersona
    ? "from-violet-400/60 via-violet-300/20 to-transparent"
    : "from-emerald-400/60 via-emerald-300/20 to-transparent";

  return (
    <div className="rounded-xl bg-card border border-border/50 overflow-hidden relative" data-testid={`card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`h-[2px] bg-gradient-to-r ${gradientBorder}`} />

      <div className="px-5 pt-4 pb-0">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`h-3.5 w-3.5 ${accentText} shrink-0`} />
          <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] ${subtleText}`}>{title}</span>
        </div>
      </div>

      <div className="px-5 pt-2 pb-5 relative">
        <Icon className={`absolute right-4 bottom-3 h-20 w-20 ${isPersona ? "text-violet-500/[0.03]" : "text-emerald-500/[0.03]"}`} />
        {summary && (
          <p className="text-[15px] text-foreground/85 leading-[1.7] relative z-10 italic">{summary}</p>
        )}
      </div>

      {allFields.length > 0 && (
        <>
          <button
            className="w-full px-5 py-2.5 flex items-center gap-2 text-[11px] text-muted-foreground/60 hover:text-foreground/70 transition-colors border-t border-border/20 group"
            onClick={() => setExpanded(!expanded)}
            data-testid={`button-expand-${title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className={`inline-flex items-center justify-center h-4 min-w-[18px] px-1 rounded text-[9px] font-bold tabular-nums ${isPersona ? "bg-violet-500/10 text-violet-500" : "bg-emerald-500/10 text-emerald-500"}`}>{allFields.length}</span>
            <span>{expanded ? "Hide details" : "Explore details"}</span>
            <ChevronDown className={`h-3 w-3 ml-auto transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
          </button>

          {expanded && (
            <div className="px-5 pb-5 border-t border-border/15">
              <div className="grid grid-cols-1 gap-0 divide-y divide-border/15">
                {allFields.map(({ label, style, value, items }) => (
                  <div key={label} className="py-3 first:pt-4">
                    <p className={`text-[10px] font-semibold uppercase tracking-[0.12em] mb-2 ${subtleText}`}>{label}</p>
                    {style === "text" && value && (
                      <p className="text-[12.5px] text-foreground/70 leading-relaxed">{value}</p>
                    )}
                    {style === "list" && items && (
                      <ul className="space-y-1.5">
                        {items.map((item, i) => (
                          <li key={i} className="text-[12px] text-foreground/65 leading-snug pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:h-[3px] before:w-[3px] before:rounded-full before:bg-current before:opacity-25">
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                    {style === "tags" && items && (
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((item, i) => (
                          <span key={i} className={`text-[10px] font-medium px-2.5 py-1 rounded-full border ${isPersona ? "border-violet-200/40 text-violet-700/60 dark:border-violet-500/20 dark:text-violet-300/60" : "border-emerald-200/40 text-emerald-700/60 dark:border-emerald-500/20 dark:text-emerald-300/60"}`}>{item}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


function formatIntersectionTitle(type: string, personaSignal: string, productSignal: string): string {
  return `${personaSignal} meets ${productSignal}`;
}

function IntersectionRow({ intersection, index }: { intersection: NonNullable<AnalyzerOutput["intersections"]>[0]; index: number }) {
  const isEven = index % 2 === 0;
  return (
    <tr className={`border-b border-border/20 last:border-0 hover:bg-violet-500/[0.03] transition-colors ${isEven ? "" : "bg-muted/15"}`} data-testid={`card-intersection-${index}`}>
      <td className="py-3.5 px-4 align-top">
        <p className="text-[12.5px] font-medium text-foreground leading-snug">{intersection.why_it_matters}</p>
      </td>
      <td className="py-3.5 px-3 align-top">
        <p className="text-[11.5px] text-foreground/55 leading-relaxed">{intersection.persona_signal}</p>
      </td>
      <td className="py-3.5 px-3 align-top">
        <p className="text-[11.5px] text-foreground/55 leading-relaxed">{intersection.product_signal}</p>
      </td>
      <td className="py-3.5 px-3 align-top text-center">
        <div className={`inline-flex items-center justify-center h-7 w-7 rounded-full text-[12px] font-bold tabular-nums ${intersection.strength >= 9 ? "bg-violet-500/10 text-violet-600 dark:text-violet-400" : "bg-muted/40 text-foreground/60"}`}>
          {intersection.strength}
        </div>
      </td>
    </tr>
  );
}

function DetailField({ icon: Icon, label, value, accent }: { icon: any; label: string; value: string; accent?: string }) {
  return (
    <div className="flex gap-3">
      <div className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${accent || "bg-muted/60"}`}>
        <Icon className="h-3.5 w-3.5 text-foreground/50" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mb-0.5">{label}</p>
        <p className="text-[13px] text-foreground/80 leading-relaxed">{value}</p>
      </div>
    </div>
  );
}

function OpportunityDetails({ opportunity }: { opportunity: OpportunityItem }) {
  return (
    <div className="rounded-xl bg-muted/30 dark:bg-muted/15 p-4 space-y-4 border border-border/40">
      <DetailField
        icon={Zap}
        label="Core Tension"
        value={opportunity.core_tension}
        accent="bg-red-50 dark:bg-red-900/20"
      />
      <DetailField
        icon={Users}
        label="Persona Reason"
        value={opportunity.persona_reason}
        accent="bg-violet-50 dark:bg-violet-900/20"
      />
      <DetailField
        icon={Package}
        label="Product Anchor"
        value={opportunity.product_anchor}
        accent="bg-blue-50 dark:bg-blue-900/20"
      />
    </div>
  );
}

function OpportunityHeader({ opportunity, expanded, onToggle }: {
  opportunity: OpportunityItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const family = OPPORTUNITY_FAMILY_MAP[opportunity.opportunity_type?.toLowerCase()] || "Conflict";
  const familyColor = FAMILY_COLORS[family] || "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <div className="flex items-start gap-3 cursor-pointer" onClick={onToggle}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <Badge className={`text-[10px] font-semibold rounded-full px-2.5 py-0.5 border ${familyColor}`}>
            {family}
          </Badge>
          <StrengthDots value={opportunity.strength} />
        </div>
        <h3 className="text-sm font-bold leading-snug tracking-tight">
          {opportunity.title}
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed mt-1">{opportunity.why_promising}</p>
      </div>
      <div className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors ${expanded ? "bg-primary/10" : "hover:bg-muted"}`}>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 text-primary" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
      </div>
    </div>
  );
}

function ActivatedOpportunityCard({ opportunity, angles, brandId, generationId, index, savedOpp }: {
  opportunity: OpportunityItem;
  angles: NarrativeAngle[];
  brandId: string;
  generationId: string;
  index: number;
  savedOpp?: SavedOpportunity;
}) {
  const [expanded, setExpanded] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { toast } = useToast();

  const family = OPPORTUNITY_FAMILY_MAP[opportunity.opportunity_type?.toLowerCase()] || "Conflict";
  const familyColor = FAMILY_COLORS[family] || "bg-gray-100 text-gray-700 border-gray-200";

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-generations/${generationId}/saved-opportunities`, {
        title: opportunity.title,
        opportunityType: opportunity.opportunity_type,
        coreTension: opportunity.core_tension,
        personaReason: opportunity.persona_reason,
        productAnchor: opportunity.product_anchor,
        whyPromising: opportunity.why_promising,
        strength: opportunity.strength,
        opportunityIndex: index,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
      toast({ title: "Opportunity saved" });
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: async () => {
      if (savedOpp) await apiRequest("DELETE", `/api/saved-opportunities/${savedOpp.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
      toast({ title: "Opportunity removed from saved" });
    },
  });

  const favMutation = useMutation({
    mutationFn: async () => {
      if (savedOpp) await apiRequest("POST", `/api/saved-opportunities/${savedOpp.id}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
    },
  });

  const isSaved = !!savedOpp;

  return (
    <Card className="border-primary/20 bg-primary/[0.01] dark:bg-primary/[0.03]" data-testid={`card-activated-opportunity-${index}`}>
      <CardContent className="p-0">
        <div className="p-4 pb-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-[15px] font-bold leading-snug tracking-tight mb-1.5">{opportunity.title}</h3>
            <div className="shrink-0 flex items-center gap-1">
              {isSaved && (
                <button
                  onClick={() => favMutation.mutate()}
                  className="p-1 hover:bg-muted/50 rounded-full transition-colors"
                  data-testid={`button-fav-opp-${index}`}
                  title={savedOpp?.isFavorite ? "Unfavorite" : "Favorite"}
                >
                  <Heart className={`h-3.5 w-3.5 ${savedOpp?.isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground/30 hover:text-muted-foreground/50"}`} />
                </button>
              )}
              <button
                onClick={() => isSaved ? unsaveMutation.mutate() : saveMutation.mutate()}
                className="p-1 hover:bg-muted/50 rounded-full transition-colors"
                data-testid={`button-save-opp-${index}`}
                title={isSaved ? "Remove from saved" : "Save opportunity"}
              >
                <Bookmark className={`h-3.5 w-3.5 ${isSaved ? "fill-primary text-primary" : "text-muted-foreground/30 hover:text-muted-foreground/50"}`} />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2.5">
            <Badge className={`text-[9px] font-semibold rounded-full px-2.5 py-0.5 border ${familyColor}`}>{family}</Badge>
            <StrengthDots value={opportunity.strength} />
          </div>
          <p className="text-[13px] text-foreground/60 leading-relaxed mb-3">{opportunity.why_promising}</p>
          <button
            className="text-[10px] text-primary/50 hover:text-primary transition-colors flex items-center gap-1"
            onClick={() => setDetailsOpen(!detailsOpen)}
          >
            {detailsOpen ? "Hide details" : "View details"}
            <ChevronDown className={`h-3 w-3 transition-transform ${detailsOpen ? "rotate-180" : ""}`} />
          </button>
        </div>

        {detailsOpen && (
          <div className="px-4 pb-3">
            <OpportunityDetails opportunity={opportunity} />
          </div>
        )}

        <div className="border-t border-primary/10">
          <button
            className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-muted/30 transition-colors"
            onClick={() => setExpanded(!expanded)}
            data-testid={`button-toggle-angles-${index}`}
          >
            <span className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              {angles.length} Narrative {angles.length === 1 ? "Angle" : "Angles"}
            </span>
            <div className={`flex h-5 w-5 items-center justify-center rounded-full transition-colors ${expanded ? "bg-primary/10" : ""}`}>
              {expanded ? <ChevronUp className="h-3 w-3 text-primary" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
            </div>
          </button>

          {expanded && (
            <div className="px-4 pb-4 grid grid-cols-1 gap-2.5" data-testid={`section-angles-for-opportunity-${index}`}>
              {sortAnglesByScore(angles).map((angle) => (
                <NarrativeAngleCard key={angle.id} angle={angle} brandId={brandId} />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SelectableOpportunityCard({
  opportunity,
  index,
  selected,
  onToggle,
  disabled,
  generationId,
  savedOpp,
}: {
  opportunity: OpportunityItem;
  index: number;
  selected: boolean;
  onToggle: () => void;
  disabled: boolean;
  generationId: string;
  savedOpp?: SavedOpportunity;
}) {
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();
  const family = OPPORTUNITY_FAMILY_MAP[opportunity.opportunity_type?.toLowerCase()] || "Conflict";
  const familyColor = FAMILY_COLORS[family] || "bg-gray-100 text-gray-700 border-gray-200";

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-generations/${generationId}/saved-opportunities`, {
        title: opportunity.title,
        opportunityType: opportunity.opportunity_type,
        coreTension: opportunity.core_tension,
        personaReason: opportunity.persona_reason,
        productAnchor: opportunity.product_anchor,
        whyPromising: opportunity.why_promising,
        strength: opportunity.strength,
        opportunityIndex: index,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
      toast({ title: "Opportunity saved" });
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: async () => {
      if (savedOpp) await apiRequest("DELETE", `/api/saved-opportunities/${savedOpp.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
      toast({ title: "Opportunity removed from saved" });
    },
  });

  const favMutation = useMutation({
    mutationFn: async () => {
      if (savedOpp) await apiRequest("POST", `/api/saved-opportunities/${savedOpp.id}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
    },
  });

  const isSaved = !!savedOpp;

  return (
    <Card
      className={`transition-all duration-200 ${
        selected
          ? "ring-2 ring-primary/80 shadow-md bg-primary/[0.02] dark:bg-primary/[0.04]"
          : "hover:shadow-md hover:border-border/80"
      }`}
      data-testid={`card-opportunity-${index}`}
    >
      <CardContent className="p-0">
        <div
          className="flex items-start gap-3 p-4 pb-3 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={onToggle}
              disabled={disabled}
              className="h-[18px] w-[18px]"
              data-testid={`checkbox-opportunity-${index}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <Badge className={`text-[10px] font-semibold rounded-full px-2.5 py-0.5 border ${familyColor}`}>
                {family}
              </Badge>
              <StrengthDots value={opportunity.strength} />
            </div>
            <h3 className="text-sm font-bold leading-snug tracking-tight" data-testid={`text-opportunity-title-${index}`}>
              {opportunity.title}
            </h3>
          </div>
          <div className="shrink-0 flex items-center gap-0.5 mt-1" onClick={(e) => e.stopPropagation()}>
            {isSaved && (
              <button
                onClick={() => favMutation.mutate()}
                className="p-1 hover:bg-muted/50 rounded-full transition-colors"
                data-testid={`button-fav-opp-${index}`}
                title={savedOpp?.isFavorite ? "Unfavorite" : "Favorite"}
              >
                <Heart className={`h-3.5 w-3.5 ${savedOpp?.isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground/30 hover:text-muted-foreground/50"}`} />
              </button>
            )}
            <button
              onClick={() => isSaved ? unsaveMutation.mutate() : saveMutation.mutate()}
              className="p-1 hover:bg-muted/50 rounded-full transition-colors"
              data-testid={`button-save-opp-sel-${index}`}
              title={isSaved ? "Remove from saved" : "Save opportunity"}
            >
              <Bookmark className={`h-3.5 w-3.5 ${isSaved ? "fill-primary text-primary" : "text-muted-foreground/30 hover:text-muted-foreground/50"}`} />
            </button>
          </div>
        </div>

        <div className="px-4 pb-3 pl-[52px]">
          <p className="text-xs text-muted-foreground leading-relaxed">{opportunity.why_promising}</p>
        </div>

        {expanded && (
          <div className="mx-4 mb-4 ml-[52px]">
            <OpportunityDetails opportunity={opportunity} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UnselectedOpportunityCard({
  opportunity,
  index,
  selected,
  onToggle,
  onGenerateOne,
  disabled,
  generationId,
  savedOpp,
}: {
  opportunity: OpportunityItem;
  index: number;
  selected: boolean;
  onToggle: () => void;
  onGenerateOne: () => void;
  disabled: boolean;
  generationId: string;
  savedOpp?: SavedOpportunity;
}) {
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();
  const family = OPPORTUNITY_FAMILY_MAP[opportunity.opportunity_type?.toLowerCase()] || "Conflict";
  const familyColor = FAMILY_COLORS[family] || "bg-gray-100 text-gray-700 border-gray-200";

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-generations/${generationId}/saved-opportunities`, {
        title: opportunity.title,
        opportunityType: opportunity.opportunity_type,
        coreTension: opportunity.core_tension,
        personaReason: opportunity.persona_reason,
        productAnchor: opportunity.product_anchor,
        whyPromising: opportunity.why_promising,
        strength: opportunity.strength,
        opportunityIndex: index,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
      toast({ title: "Opportunity saved" });
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: async () => {
      if (savedOpp) await apiRequest("DELETE", `/api/saved-opportunities/${savedOpp.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
      toast({ title: "Opportunity removed from saved" });
    },
  });

  const favMutation = useMutation({
    mutationFn: async () => {
      if (savedOpp) await apiRequest("POST", `/api/saved-opportunities/${savedOpp.id}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
    },
  });

  const isSaved = !!savedOpp;

  return (
    <Card
      className={`transition-all duration-200 ${
        selected
          ? "ring-2 ring-primary/80 shadow-md bg-primary/[0.02] dark:bg-primary/[0.04]"
          : "hover:shadow-md hover:border-border/80"
      }`}
      data-testid={`card-unselected-opportunity-${index}`}
    >
      <CardContent className="p-0">
        <div
          className="flex items-start gap-3 p-4 pb-3 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="pt-1" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={onToggle}
              disabled={disabled}
              className="h-[18px] w-[18px]"
              data-testid={`checkbox-unselected-opportunity-${index}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <Badge className={`text-[10px] font-semibold rounded-full px-2.5 py-0.5 border ${familyColor}`}>
                {family}
              </Badge>
              <StrengthDots value={opportunity.strength} />
            </div>
            <h3 className="text-sm font-bold leading-snug tracking-tight" data-testid={`text-unselected-opportunity-title-${index}`}>
              {opportunity.title}
            </h3>
          </div>
          <div className="shrink-0 flex items-center gap-0.5 mt-1" onClick={(e) => e.stopPropagation()}>
            {isSaved && (
              <button
                onClick={() => favMutation.mutate()}
                className="p-1 hover:bg-muted/50 rounded-full transition-colors"
                data-testid={`button-fav-unsel-opp-${index}`}
                title={savedOpp?.isFavorite ? "Unfavorite" : "Favorite"}
              >
                <Heart className={`h-3.5 w-3.5 ${savedOpp?.isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground/30 hover:text-muted-foreground/50"}`} />
              </button>
            )}
            <button
              onClick={() => isSaved ? unsaveMutation.mutate() : saveMutation.mutate()}
              className="p-1 hover:bg-muted/50 rounded-full transition-colors"
              data-testid={`button-save-unsel-opp-${index}`}
              title={isSaved ? "Remove from saved" : "Save opportunity"}
            >
              <Bookmark className={`h-3.5 w-3.5 ${isSaved ? "fill-primary text-primary" : "text-muted-foreground/30 hover:text-muted-foreground/50"}`} />
            </button>
          </div>
        </div>

        <div className="px-4 pb-3 pl-[52px]">
          <p className="text-xs text-muted-foreground leading-relaxed">{opportunity.why_promising}</p>
        </div>

        {expanded && (
          <div className="mx-4 mb-3 ml-[52px] space-y-3">
            <OpportunityDetails opportunity={opportunity} />
            <Button
              variant="outline"
              size="sm"
              className="rounded-full w-full"
              onClick={(e) => {
                e.stopPropagation();
                onGenerateOne();
              }}
              disabled={disabled}
              data-testid={`button-generate-one-${index}`}
            >
              {disabled ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                  Generate This One
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReadinessSection({ data }: { data: AnalyzerOutput["readiness_assessment"] }) {
  if (!data) return null;
  const scorePct = (data.overall_score / 10) * 100;
  const scoreColor = data.overall_score >= 8 ? "text-emerald-600 dark:text-emerald-400" : data.overall_score >= 5 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";
  const barColor = data.overall_score >= 8 ? "bg-emerald-500" : data.overall_score >= 5 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="rounded-2xl border bg-card overflow-hidden" data-testid="card-readiness">
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-muted/60 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-foreground/60" />
            </div>
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/70">Readiness Assessment</h2>
              <p className="text-[10px] text-muted-foreground/50 mt-0.5">{data.ready_for_generation ? "Ready for angle generation" : "May need additional input"}</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold tabular-nums ${scoreColor}`}>{data.overall_score}</p>
            <p className="text-[9px] text-muted-foreground/40 uppercase tracking-wider">of 10</p>
          </div>
        </div>

        <div className="h-1 bg-muted rounded-full overflow-hidden mt-4">
          <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${scorePct}%` }} />
        </div>
      </div>

      {(Array.isArray(data.strengths) && data.strengths.length > 0 || Array.isArray(data.warnings) && data.warnings.length > 0) && (
        <div className="border-t border-border/40 px-5 py-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.isArray(data.strengths) && data.strengths.length > 0 && (
            <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500/60 mb-1.5">Strengths</p>
            <ul className="space-y-1">
              {data.strengths.map((s, i) => (
                <li key={i} className="text-[12px] text-foreground/65 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:h-[3px] before:w-[3px] before:rounded-full before:bg-emerald-400/60">
                  {typeof s === "string" ? s : (s as any)?.description || JSON.stringify(s)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(data.warnings) && data.warnings.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-500/60 mb-1.5">Warnings</p>
            <ul className="space-y-1">
              {data.warnings.map((w, i) => (
                <li key={i} className="text-[12px] text-foreground/65 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:h-[3px] before:w-[3px] before:rounded-full before:bg-amber-400/60">
                  {typeof w === "string" ? w : (w as any)?.description || (w as any)?.recommendation || JSON.stringify(w)}
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>
      )}
    </div>
  );
}

function SignalFlowAnimation() {
  return (
    <div className="relative h-16 w-full overflow-hidden" data-testid="signal-flow-animation">
      <svg
        viewBox="0 0 800 64"
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="grad-persona" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgb(139 92 246)" stopOpacity="0" />
            <stop offset="80%" stopColor="rgb(139 92 246)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="rgb(139 92 246)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="grad-product" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0" />
            <stop offset="80%" stopColor="rgb(16 185 129)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="grad-center" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgb(139 92 246)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="rgb(139 92 246)" stopOpacity="0" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          d="M 100 12 Q 250 12, 400 32"
          fill="none"
          stroke="rgb(139 92 246)"
          strokeOpacity="0.08"
          strokeWidth="1"
        />
        <path
          d="M 700 12 Q 550 12, 400 32"
          fill="none"
          stroke="rgb(16 185 129)"
          strokeOpacity="0.08"
          strokeWidth="1"
        />
        <path
          d="M 400 32 L 400 58"
          fill="none"
          stroke="rgb(139 92 246)"
          strokeOpacity="0.08"
          strokeWidth="1"
        />

        {[0, 1, 2].map(i => (
          <circle key={`p${i}`} r="2" fill="rgb(139 92 246)" filter="url(#glow)">
            <animateMotion
              dur="3s"
              repeatCount="indefinite"
              begin={`${i * 1}s`}
              path="M 100 12 Q 250 12, 400 32 L 400 58"
            />
            <animate attributeName="opacity" values="0;0.7;0.7;0" dur="3s" repeatCount="indefinite" begin={`${i * 1}s`} />
          </circle>
        ))}

        {[0, 1, 2].map(i => (
          <circle key={`e${i}`} r="2" fill="rgb(16 185 129)" filter="url(#glow)">
            <animateMotion
              dur="3s"
              repeatCount="indefinite"
              begin={`${i * 1 + 0.5}s`}
              path="M 700 12 Q 550 12, 400 32 L 400 58"
            />
            <animate attributeName="opacity" values="0;0.7;0.7;0" dur="3s" repeatCount="indefinite" begin={`${i * 1 + 0.5}s`} />
          </circle>
        ))}

        <circle cx="400" cy="32" r="4" fill="rgb(139 92 246)" fillOpacity="0.15">
          <animate attributeName="r" values="3;6;3" dur="2s" repeatCount="indefinite" />
          <animate attributeName="fill-opacity" values="0.2;0.08;0.2" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="400" cy="32" r="2" fill="rgb(139 92 246)" fillOpacity="0.4">
          <animate attributeName="fill-opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
}

function IntersectionsSection({ intersections }: { intersections: AnalyzerOutput["intersections"] }) {
  const [showAll, setShowAll] = useState(false);
  if (!intersections || intersections.length === 0) return null;

  const sorted = [...intersections].sort((a, b) => b.strength - a.strength);
  const visible = showAll ? sorted : sorted.slice(0, 10);

  return (
    <div data-testid="section-intersections">
      <div className="rounded-xl border border-border/40 overflow-hidden bg-card">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border/40 bg-muted/30">
              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 py-2.5 px-4 w-[40%]">Insight</th>
              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-violet-500/60 py-2.5 px-3 w-[25%]">Persona Signal</th>
              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-emerald-500/60 py-2.5 px-3 w-[25%]">Product Signal</th>
              <th className="text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 py-2.5 px-3 w-[10%]">Score</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((intersection, i) => (
              <IntersectionRow key={i} intersection={intersection} index={i} />
            ))}
          </tbody>
        </table>
        {intersections.length > 10 && (
          <button
            className="w-full py-2.5 flex items-center justify-center gap-1.5 text-[11px] font-medium text-muted-foreground/60 hover:text-foreground/70 transition-colors border-t border-border/30"
            onClick={() => setShowAll(!showAll)}
            data-testid="button-toggle-intersections"
          >
            {showAll ? "Show fewer" : `View all ${intersections.length}`}
            <ChevronDown className={`h-3 w-3 transition-transform ${showAll ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
    </div>
  );
}

function matchAnglesToOpportunity(
  opportunity: OpportunityItem,
  angles: NarrativeAngle[],
  grouping?: AngleGroupingEntry[] | null,
): NarrativeAngle[] {
  if (grouping && grouping.length > 0) {
    const matchingGroups = grouping.filter(g =>
      (g.source_opportunity_id && g.source_opportunity_id === (opportunity as any).id) ||
      g.source_opportunity_title === opportunity.title ||
      (g.source_opportunity_type === opportunity.opportunity_type && g.source_opportunity_title === opportunity.title)
    );

    if (matchingGroups.length > 0) {
      const nameSet = new Set<string>();
      for (const group of matchingGroups) {
        for (const n of group.angle_names) {
          nameSet.add(n.toLowerCase());
        }
      }

      const matched = angles.filter(a => nameSet.has(a.name.toLowerCase()));
      if (matched.length > 0) return matched;

      const sourceTitle = matchingGroups[0].source_opportunity_title;
      const byNotes = angles.filter(a => a.notes === sourceTitle);
      if (byNotes.length > 0) return byNotes;
    }
  }

  const titleLower = opportunity.title.toLowerCase();
  return angles.filter(a => {
    if (a.notes === opportunity.title) return true;
    if (a.name?.toLowerCase().includes(titleLower)) return true;
    const nameStart = opportunity.title.split(" ").slice(0, 3).join(" ").toLowerCase();
    if (a.name?.toLowerCase().includes(nameStart)) return true;
    if (a.coreTension && a.coreTension === opportunity.core_tension) return true;
    return false;
  });
}

function SaveOpportunityButton({ opp, generationId, saved }: { opp: OpportunityItem; generationId: string; saved?: SavedOpportunity }) {
  const { toast } = useToast();
  const isSaved = !!saved;

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-generations/${generationId}/saved-opportunities`, {
        title: opp.title,
        opportunityType: opp.opportunity_type,
        coreTension: opp.core_tension,
        personaReason: opp.persona_reason,
        productAnchor: opp.product_anchor,
        whyPromising: opp.why_promising,
        strength: opp.strength,
        opportunityIndex: 0,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
      toast({ title: "Opportunity saved" });
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: async () => {
      if (saved) await apiRequest("DELETE", `/api/saved-opportunities/${saved.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'] });
      toast({ title: "Removed from saved" });
    },
  });

  return (
    <button
      onClick={() => isSaved ? unsaveMutation.mutate() : saveMutation.mutate()}
      className="p-1 hover:bg-muted/50 rounded-full transition-colors"
      title={isSaved ? "Remove from saved" : "Save opportunity"}
      data-testid={`button-fav-opportunity-${opp.title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <Heart className={`h-3.5 w-3.5 ${isSaved ? "fill-red-500 text-red-500" : "text-muted-foreground/40 hover:text-red-400"}`} />
    </button>
  );
}

function ScorePill({ label, value, color }: { label: string; value: number | null; color: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-[10px] font-medium ${color}`}>{label}</span>
      <span className="text-[12px] font-bold tabular-nums text-foreground/70">{value ?? "–"}</span>
    </div>
  );
}

function AngleTable({ angles, brandId }: { angles: NarrativeAngle[]; brandId: string }) {
  const { toast } = useToast();

  return (
    <div className="space-y-2">
      {angles.map((angle) => {
        const scores = [angle.resonanceScore, angle.proofScore, angle.distinctivenessScore, angle.emotionalScore].filter((s): s is number => s !== null);
        const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
        return (
          <div key={angle.id} className="rounded-xl border border-border/40 bg-card p-4 hover:bg-muted/20 transition-colors" data-testid={`row-angle-${angle.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Link href={`/brands/${brandId}/narrative-angles/${angle.id}`}>
                  <span className="text-[13px] font-semibold text-foreground hover:text-primary transition-colors cursor-pointer">{angle.name}</span>
                </Link>
                {angle.family && (
                  <Badge className={`text-[9px] rounded-full px-2 py-0 ${FAMILY_COLORS[angle.family] || "bg-gray-100 text-gray-700"}`}>
                    {angle.family}
                  </Badge>
                )}
                <NarrativeFunnelStageBadge stage={angle.funnelStage} />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <AngleFavButton angle={angle} brandId={brandId} />
                <AngleDeleteButton angle={angle} brandId={brandId} />
              </div>
            </div>

            <p className="text-[12px] text-muted-foreground leading-relaxed mb-3">{angle.summary}</p>

            <div className="flex items-center gap-4 flex-wrap">
              <ScorePill label="Resonance" value={angle.resonanceScore} color="text-violet-500/80" />
              <ScorePill label="Proof" value={angle.proofScore} color="text-emerald-500/80" />
              <ScorePill label="Distinctiveness" value={angle.distinctivenessScore} color="text-amber-500/80" />
              <ScorePill label="Emotional" value={angle.emotionalScore} color="text-rose-500/80" />
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-[10px] font-medium text-muted-foreground/60">Average</span>
                <span className="text-[13px] font-bold tabular-nums text-primary">{avg > 0 ? avg.toFixed(1) : "–"}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AngleFavButton({ angle, brandId }: { angle: NarrativeAngle; brandId: string }) {
  const favMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-angles/${angle.id}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-generations`] });
      if (angle.generationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${angle.generationId}`] });
      }
    },
  });
  return (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); favMutation.mutate(); }}
      className="p-1 hover:bg-muted/50 rounded-full transition-colors"
      data-testid={`button-fav-${angle.id}`}
    >
      <Heart className={`h-3.5 w-3.5 ${angle.isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground/40 hover:text-red-400"}`} />
    </button>
  );
}

function AngleDeleteButton({ angle, brandId }: { angle: NarrativeAngle; brandId: string }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/narrative-angles/${angle.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-generations`] });
      if (angle.generationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${angle.generationId}`] });
      }
      setOpen(false);
      toast({ title: "Angle deleted" });
    },
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); }}
          className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
          data-testid={`button-delete-angle-${angle.id}`}
        >
          <Trash2 className="h-3.5 w-3.5 text-muted-foreground/40 hover:text-red-500" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this angle?</AlertDialogTitle>
          <AlertDialogDescription>
            "{angle.name}" will be removed from this analysis. This can't be undone from the UI.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`button-cancel-delete-angle-${angle.id}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); deleteMutation.mutate(); }}
            disabled={deleteMutation.isPending}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            data-testid={`button-confirm-delete-angle-${angle.id}`}
          >
            {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteOpportunityButton({ opp, generationId }: { opp: OpportunityItem; generationId: string }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-generations/${generationId}/hide-opportunity`, { title: opp.title });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${generationId}`] });
      setOpen(false);
      toast({ title: "Opportunity deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to delete opportunity", description: err?.message, variant: "destructive" });
    },
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); }}
          className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
          title="Delete opportunity"
          data-testid={`button-delete-opportunity-${opp.title.replace(/\s+/g, '-').toLowerCase()}`}
        >
          <Trash2 className="h-3.5 w-3.5 text-muted-foreground/40 hover:text-red-500" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this opportunity?</AlertDialogTitle>
          <AlertDialogDescription>
            "{opp.title}" will be removed from this analysis. Any angles already generated for it will remain.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); deleteMutation.mutate(); }}
            disabled={deleteMutation.isPending}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            data-testid="button-confirm-delete-opportunity"
          >
            {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function NarrativeGenerationDetailPage() {
  const { brandId, generationId } = useParams<{ brandId: string; generationId: string }>();
  const { toast } = useToast();
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [showUnselected, setShowUnselected] = useState(false);
  const [angleGenStartedAt, setAngleGenStartedAt] = useState<Date | null>(null);
  const [showAddOppDialog, setShowAddOppDialog] = useState(false);
  const [oppUserInput, setOppUserInput] = useState("");
  const [expandedAngleClusters, setExpandedAngleClusters] = useState<Set<string>>(new Set());

  const toggleAngleCluster = (title: string) => {
    setExpandedAngleClusters((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const { data: generation, isLoading } = useQuery<GenerationWithData>({
    queryKey: [`/api/narrative-generations/${generationId}`],
    queryFn: getQueryFn({ on401: "throw" }),
    refetchInterval: (query) => {
      const gen = query.state.data;
      if (gen && (gen.status === "queued" || gen.status === "running" || gen.status === "generating_angles")) return 3000;
      return false;
    },
  });

  const { data: persona } = useQuery<Persona>({
    queryKey: ["/api/personas", generation?.personaId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!generation?.personaId,
  });

  const { data: product } = useQuery<Product>({
    queryKey: ["/api/products", generation?.productId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!generation?.productId,
  });

  const { data: offering } = useQuery<Offer>({
    queryKey: ["/api/offers", generation?.offeringId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!generation?.offeringId,
  });

  const isOffering = generation?.promotedItemType === "offering";
  const promotedName = isOffering ? offering?.name : product?.name;
  const promotedLabel = isOffering ? "Offering" : "Product";

  const { data: reviewStats } = useQuery<Record<string, { count: number; avgRating: number | null }>>({
    queryKey: ["/api/brands", brandId, "product-review-stats"],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!brandId,
  });

  const { data: allGenerations = [] } = useQuery<NarrativeGeneration[]>({
    queryKey: [`/api/brands/${brandId}/narrative-generations`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: savedOpportunities = [] } = useQuery<SavedOpportunity[]>({
    queryKey: ['/api/narrative-generations', generationId, 'saved-opportunities'],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!generationId,
  });

  const retryMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/narrative-generations/${generationId}/retry`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${generationId}`] });
      toast({ title: "Retrying generation" });
    },
    onError: (e: Error) => {
      toast({ title: "Retry failed", description: e.message, variant: "destructive" });
    },
  });

  const angleQueueRef = useRef(createNarrativeAngleQueue());
  const requestAngleOpportunity = (opp: OpportunityItem) => angleQueueRef.current(async () => {
    const response = await apiRequest("POST", `/api/narrative-generations/${generationId}/generate-angles`, {
      selectedOpportunities: [opp],
    });
    return response.json();
  });

  const generateAnglesMutation = useMutation({
    mutationFn: async (selectedOpportunities: OpportunityItem[]) => {
      setAngleGenStartedAt(new Date());
      // Queue one request per opportunity under the server's generation lease.
      // Each result stays independent; failure must not cancel later requests.
      const results = await Promise.allSettled(
        selectedOpportunities.map(requestAngleOpportunity),
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;
      const firstError = results.find((r) => r.status === "rejected") as
        | PromiseRejectedResult
        | undefined;
      return { succeeded, failed, total: results.length, firstError };
    },
    onSuccess: ({ succeeded, failed, total, firstError }) => {
      queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${generationId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      setSelectedIndices(new Set());
      setAngleGenStartedAt(null);
      if (failed === 0) {
        toast({
          title: total === 1 ? "Narrative angles generated" : `Generated angles for ${succeeded} opportunities`,
        });
      } else if (succeeded === 0) {
        toast({
          title: "Angle generation failed",
          description: firstError?.reason?.message || "All opportunities failed to generate angles.",
          variant: "destructive",
        });
      } else {
        toast({
          title: `Generated ${succeeded} of ${total}`,
          description: `${failed} ${failed === 1 ? "opportunity" : "opportunities"} failed. ${firstError?.reason?.message || "You can retry the failed ones."}`,
          variant: "destructive",
        });
      }
    },
    onError: (e: Error) => {
      setAngleGenStartedAt(null);
      toast({ title: "Angle generation failed", description: e.message, variant: "destructive" });
    },
  });

  const relatedGenerations = allGenerations.filter(
    g => g.id !== generationId && g.personaId === generation?.personaId && g.productId === generation?.productId
  );

  const toggleOpportunity = (index: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleGenerateAngles = () => {
    const analyzerOutput = generation?.parsedOutputJson as AnalyzerOutput | null;
    const opportunities = analyzerOutput?.top_opportunities || [];
    const selected = Array.from(selectedIndices)
      .sort((a, b) => a - b)
      .map(i => opportunities[i])
      .filter(Boolean);

    if (selected.length === 0) return;
    generateAnglesMutation.mutate(selected);
  };

  const [inFlightOppKeys, setInFlightOppKeys] = useState<Set<string>>(new Set());
  const inFlightOppKeysRef = useRef<Set<string>>(new Set());
  const isOppRunning = (opp: OpportunityItem) => inFlightOppKeys.has(opp.title);

  const runSingleOpportunity = async (opp: OpportunityItem) => {
    const key = opp.title;
    if (inFlightOppKeysRef.current.has(key)) return;
    inFlightOppKeysRef.current.add(key);
    setInFlightOppKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setAngleGenStartedAt((prev) => prev ?? new Date());
    try {
      await requestAngleOpportunity(opp);
      queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${generationId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      toast({ title: "Narrative angles generated", description: opp.title });
    } catch (e: any) {
      toast({ title: "Angle generation failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      inFlightOppKeysRef.current.delete(key);
      setInFlightOppKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const addOpportunityMutation = useMutation({
    mutationFn: async (userInput: string) => {
      const res = await apiRequest("POST", `/api/narrative-generations/${generationId}/add-opportunity`, { userInput });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${generationId}`] });
      setShowAddOppDialog(false);
      setOppUserInput("");
      toast({ title: "Opportunity created", description: "Your custom opportunity has been generated and added." });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to create opportunity", description: e.message, variant: "destructive" });
    },
  });

  const anyMutationPending = generateAnglesMutation.isPending || inFlightOppKeys.size > 0;

  useEffect(() => {
    if (!generateAnglesMutation.isPending && inFlightOppKeys.size === 0) {
      setAngleGenStartedAt(null);
    }
  }, [generateAnglesMutation.isPending, inFlightOppKeys]);

  const selectAllUnactivated = () => {
    const analyzerOutput = generation?.parsedOutputJson as AnalyzerOutput | null;
    const opportunities = analyzerOutput?.top_opportunities || [];
    const angles = generation?.angles || [];
    const grouping = generation?.angleGroupingJson;

    const unactivatedIndices = opportunities
      .map((opp, i) => ({ opp, i }))
      .filter(({ opp }) => matchAnglesToOpportunity(opp, angles, grouping).length === 0)
      .map(({ i }) => i);

    if (selectedIndices.size === unactivatedIndices.length && unactivatedIndices.every(i => selectedIndices.has(i))) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(unactivatedIndices));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!generation) return <div className="p-8">Generation not found</div>;

  const analyzerOutput = generation.parsedOutputJson as AnalyzerOutput | null;
  const opportunities = analyzerOutput?.top_opportunities || [];
  const angles = generation.angles || [];
  const grouping = generation.angleGroupingJson;

  const activatedOpps: Array<{ opp: OpportunityItem; index: number; angles: NarrativeAngle[] }> = [];
  const unactivatedOpps: Array<{ opp: OpportunityItem; index: number }> = [];
  const unmatchedAngles: NarrativeAngle[] = [];
  const matchedAngleIds = new Set<string>();

  opportunities.forEach((opp, i) => {
    const matched = matchAnglesToOpportunity(opp, angles, grouping);
    if (matched.length > 0) {
      matched.forEach(a => matchedAngleIds.add(a.id));
      activatedOpps.push({ opp, index: i, angles: matched });
    } else {
      unactivatedOpps.push({ opp, index: i });
    }
  });

  angles.forEach(a => {
    if (!matchedAngleIds.has(a.id)) {
      unmatchedAngles.push(a);
    }
  });

  const savedOppByTitle = new Map<string, SavedOpportunity>();
  savedOpportunities.forEach(so => savedOppByTitle.set(so.title, so));

  const selectedUnactivated = unactivatedOpps.filter(({ index }) => selectedIndices.has(index));
  const hasActivated = activatedOpps.length > 0;
  const hasSelected = selectedUnactivated.length > 0;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-8">
      <div className="fade-in-up">
        <div className="flex items-center gap-2 mb-4">
          <Link href={`/brands/${brandId}/narratives`}>
            <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="button-back">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
          </Link>
          <ChevronRight className="h-3 w-3 text-muted-foreground/40" />
          <span className="text-xs text-muted-foreground">Analysis</span>
        </div>

        <div className="rounded-2xl border bg-card overflow-hidden shadow-sm">
          <div className="bg-gradient-to-r from-violet-500/10 via-primary/5 to-emerald-500/10 px-6 md:px-8 pt-5 pb-4 border-b border-border/30">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">{isOffering ? "Persona–Offering Analysis" : "Persona–Product Analysis"}</p>
                <GenerationStatusBadge status={generation.status} />
              </div>
              {generation.status === "failed" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => retryMutation.mutate()}
                  disabled={retryMutation.isPending || !generation.personaId}
                  data-testid="button-retry"
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${retryMutation.isPending ? "animate-spin" : ""}`} />
                  Retry
                </Button>
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3 flex-wrap mt-3" data-testid="text-page-title">
              <span className="flex items-center gap-2">
                {persona?.imageUrl ? (
                  <img src={optimizedImg(persona.imageUrl, 32)} alt={persona.name} className="h-8 w-8 rounded-lg object-cover" data-testid="img-persona-header" />
                ) : (
                  <div className="h-8 w-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <Users className="h-4 w-4 text-violet-500" />
                  </div>
                )}
                {persona?.name || (generation.personaId ? "Loading..." : "Persona deleted")}
              </span>
              <span className="text-muted-foreground/25 font-light text-lg">&times;</span>
              <span className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  {isOffering ? <Tag className="h-4 w-4 text-emerald-500" /> : <Package className="h-4 w-4 text-emerald-500" />}
                </div>
                {promotedName || "..."}
              </span>
            </h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border/30">
            <div className="px-6 md:px-8 py-4 space-y-2.5">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-md bg-violet-500/10 flex items-center justify-center">
                  <Users className="h-3 w-3 text-violet-500" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-violet-600 dark:text-violet-400">Persona</p>
              </div>
              <div className="flex gap-3">
                {persona?.imageUrl && (
                  <div className="flex-shrink-0">
                    <img
                      src={optimizedImg(persona.imageUrl, 64)}
                      alt={persona?.name || "Persona"}
                      className="h-16 w-16 rounded-lg object-cover border border-border/40 shadow-sm"
                      data-testid="img-persona-hero"
                    />
                  </div>
                )}
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-semibold text-foreground">{persona?.name || (generation.personaId ? "Loading..." : "Persona deleted")}</p>
                  {persona?.summary && (
                    <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-2">{persona.summary}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-0.5">
                {persona?.awarenessStage && (
                  <span className="text-[10px] font-medium bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 px-2 py-0.5 rounded-full">
                    {persona.awarenessStage.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                )}
                {persona?.worldview && (
                  <span className="text-[10px] font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-full truncate max-w-[200px]">
                    {persona.worldview.split(/[.!]/)[0]}
                  </span>
                )}
              </div>
            </div>

            <div className="px-6 md:px-8 py-4 space-y-2.5">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-md bg-emerald-500/10 flex items-center justify-center">
                  {isOffering ? <Tag className="h-3 w-3 text-emerald-500" /> : <Package className="h-3 w-3 text-emerald-500" />}
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">{isOffering ? "Offering" : "Product"}</p>
              </div>
              {isOffering ? (
                <>
                  <div className="flex gap-3">
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <p className="text-sm font-semibold text-foreground" data-testid="text-offering-name">{offering?.name || (generation?.offeringId ? "Loading..." : "—")}</p>
                      {(offering?.shortDescription || offering?.summary) && (
                        <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-2">{offering?.shortDescription || offering?.summary}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-0.5">
                    {offering?.offerCategory && (
                      <span className="text-[10px] font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">
                        {offering.offerCategory}
                      </span>
                    )}
                    {offering?.offerType && (
                      <span className="text-[10px] font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                        {offering.offerType}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-3">
                    {(() => {
                      const images = product?.mediaJson as string[] | null;
                      const firstImage = images && images.length > 0 ? images[0] : null;
                      return firstImage ? (
                        <div className="flex-shrink-0">
                          <img
                            src={firstImage}
                            alt={product?.name || "Product"}
                            className="h-16 w-16 rounded-lg object-cover border border-border/40 shadow-sm"
                            data-testid="img-product-hero"
                          />
                        </div>
                      ) : null;
                    })()}
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <p className="text-sm font-semibold text-foreground">{product?.name || "Loading..."}</p>
                      {(() => {
                        const stats = product?.id && reviewStats ? reviewStats[product.id] : null;
                        if (!stats || !stats.avgRating) return null;
                        return (
                          <div className="flex items-center gap-1.5" data-testid="product-rating">
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-3 w-3 ${
                                    i < Math.round(stats.avgRating!)
                                      ? "text-amber-400 fill-amber-400"
                                      : "text-muted-foreground/30"
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-[11px] font-medium text-foreground">{stats.avgRating}</span>
                            <span className="text-[10px] text-muted-foreground">({stats.count} reviews)</span>
                          </div>
                        );
                      })()}
                      {product?.summary && (
                        <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-2">{product.summary}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-0.5">
                    {product?.category && (
                      <span className="text-[10px] font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">
                        {product.category}
                      </span>
                    )}
                    {product?.priceText && (
                      <span className="text-[10px] font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                        {product.priceText.startsWith("$") ? product.priceText : `$${product.priceText}`}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {generation.createdAt && (
            <div className="px-6 md:px-8 py-2.5 border-t border-border/30 bg-muted/20 flex items-center gap-4 text-[10px] text-muted-foreground/60">
              <span>Analyzed {new Date(generation.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
              {opportunities.length > 0 && (
                <>
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/20" />
                  <span>{opportunities.length} opportunities found</span>
                </>
              )}
              {angles.length > 0 && (
                <>
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/20" />
                  <span>{angles.length} narrative angles</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {generation.parseError && (
        <div className="rounded-xl border border-amber-300/50 bg-amber-50 dark:bg-amber-900/10 p-4 text-sm text-amber-700 dark:text-amber-400 fade-in-up" data-testid="text-parse-error">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4" />
            <span className="font-medium">Parse Warning</span>
          </div>
          {generation.parseError}
        </div>
      )}

      <GenerationWizard
        mode="analysis"
        itemType={isOffering ? "offering" : "product"}
        isRunning={generation.status === "queued" || generation.status === "running"}
        hasFailed={generation.status === "failed"}
        persistedStatus={generation.status as "queued" | "running" | "completed" | "failed"}
        errorMessage={generation.errorMessage}
        startedAt={generation.startedAt || generation.createdAt}
        title={`${persona?.name || "Persona"} × ${promotedName || promotedLabel}`}
        subtitle={isOffering ? "Persona-Offering Analysis" : "Persona-Product Analysis"}
        onRetry={() => retryMutation.mutate()}
      />

      <GenerationWizard
        mode="angles"
        itemType={isOffering ? "offering" : "product"}
        isRunning={anyMutationPending || generation.status === "generating_angles"}
        startedAt={angleGenStartedAt}
        title={`${persona?.name || "Persona"} × ${promotedName || promotedLabel}`}
        subtitle="Narrative Angle Generation"
      />

      {(generation.status === "completed" || generation.status === "generating_angles") && analyzerOutput && (
        <div className="fade-in-up">
          <div className="grid grid-cols-3 divide-x divide-border/30 rounded-xl border border-border/40 bg-card mb-5">
            <div className="px-4 py-3 text-center">
              <p className="text-lg font-bold text-foreground tabular-nums">{opportunities.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium">Opportunities</p>
            </div>
            <div className="px-4 py-3 text-center">
              <p className="text-lg font-bold text-foreground tabular-nums">{analyzerOutput.intersections?.length || 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium">Intersections</p>
            </div>
            <div className="px-4 py-3 text-center">
              <p className="text-lg font-bold text-foreground tabular-nums">{angles.length}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium">Angles</p>
            </div>
          </div>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent" data-testid="tabs-navigation">
              <TabsTrigger
                value="overview"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-xs font-medium"
                data-testid="tab-overview"
              >
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Overview
              </TabsTrigger>
              <TabsTrigger
                value="intersections"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-xs font-medium"
                data-testid="tab-intersections"
              >
                <Link2 className="h-3.5 w-3.5 mr-1.5" />
                Intersections
              </TabsTrigger>
              <TabsTrigger
                value="opportunities"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-xs font-medium"
                data-testid="tab-opportunities"
              >
                <Target className="h-3.5 w-3.5 mr-1.5" />
                Opportunities
              </TabsTrigger>
              {angles.length > 0 && (
                <TabsTrigger
                  value="angles"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-xs font-medium"
                  data-testid="tab-angles"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Angles
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview" className="mt-5 space-y-5">
              <ReadinessSection data={analyzerOutput.readiness_assessment} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CoreSection data={analyzerOutput.persona_core} icon={Users} title="Persona Core" />
                <CoreSection data={analyzerOutput.product_core} icon={Package} title="Product Core" />
              </div>
            </TabsContent>

            <TabsContent value="intersections" className="mt-5 space-y-5">
              {analyzerOutput.intersections && analyzerOutput.intersections.length > 0 && (
                <SignalFlowAnimation />
              )}
              <IntersectionsSection intersections={analyzerOutput.intersections} />
            </TabsContent>

            <TabsContent value="opportunities" className="mt-5 space-y-4">
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-[11px] h-7 rounded-full"
                  onClick={() => setShowAddOppDialog(true)}
                  data-testid="button-add-opportunity"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add My Own
                </Button>
                {unactivatedOpps.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[11px] h-7 rounded-full"
                    onClick={selectAllUnactivated}
                    disabled={anyMutationPending}
                    data-testid="button-select-all"
                  >
                    {selectedUnactivated.length === unactivatedOpps.length ? "Deselect All" : "Select Inactive"}
                  </Button>
                )}
                {hasSelected && (
                  <Button
                    size="sm"
                    className="text-[11px] h-7 rounded-full"
                    onClick={handleGenerateAngles}
                    disabled={anyMutationPending}
                    data-testid="button-generate-angles"
                  >
                    {anyMutationPending ? (
                      <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating...</>
                    ) : (
                      <><Wand2 className="h-3 w-3 mr-1" /> Activate ({selectedIndices.size})</>
                    )}
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {opportunities.map((opp, i) => {
                  const matched = matchAnglesToOpportunity(opp, angles, grouping);
                  const isActive = matched.length > 0;
                  const isSelected = selectedIndices.has(i);
                  const saved = savedOppByTitle.get(opp.title);
                  const family = OPPORTUNITY_FAMILY_MAP[opp.opportunity_type?.toLowerCase()] || "Conflict";
                  const familyColor = FAMILY_COLORS[family] || "bg-gray-100 text-gray-700";
                  return (
                    <div
                      key={i}
                      className={`rounded-xl border border-border/40 bg-card p-4 transition-colors ${isSelected ? "bg-primary/[0.04] border-primary/20" : ""} hover:bg-muted/20`}
                      data-testid={`row-opportunity-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="pt-0.5 shrink-0">
                          {!isActive ? (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleOpportunity(i)}
                              disabled={anyMutationPending}
                              className="h-3.5 w-3.5"
                              data-testid={`checkbox-opp-${i}`}
                            />
                          ) : (
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[13px] font-semibold text-foreground leading-tight">{opp.title}</span>
                              <Badge className={`text-[8px] rounded-full px-1.5 py-0 shrink-0 ${familyColor}`}>
                                {family}
                              </Badge>
                              {isActive && matched.length > 0 && (
                                <span className="text-[10px] text-emerald-600/60 dark:text-emerald-400/60">{matched.length} angles generated</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {!isActive && (
                                <button
                                  onClick={() => runSingleOpportunity(opp)}
                                  disabled={isOppRunning(opp) || generateAnglesMutation.isPending}
                                  className="text-[10px] font-semibold text-primary hover:text-primary/70 transition-colors disabled:opacity-40 inline-flex items-center gap-1"
                                  data-testid={`button-activate-${i}`}
                                >
                                  {isOppRunning(opp) ? (
                                    <><Loader2 className="h-3 w-3 animate-spin" /> Activating...</>
                                  ) : (
                                    "Activate"
                                  )}
                                </button>
                              )}
                              <DeleteOpportunityButton opp={opp} generationId={generationId!} />
                            </div>
                          </div>
                          {(opp.core_tension || opp.why_promising) && (
                            <p className="text-[12px] text-muted-foreground leading-relaxed">{opp.core_tension || opp.why_promising}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {angles.length > 0 && (
              <TabsContent value="angles" className="mt-5 space-y-5">
                {activatedOpps.length > 1 && (
                  <div className="flex items-center justify-end px-1 -mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[10px] h-6 rounded-full text-muted-foreground/70 hover:text-primary"
                      onClick={() => {
                        const allTitles = activatedOpps.map(({ opp }) => opp.title);
                        const allExpanded = allTitles.every((t) => expandedAngleClusters.has(t));
                        setExpandedAngleClusters(allExpanded ? new Set() : new Set(allTitles));
                      }}
                      data-testid="button-toggle-all-angle-clusters"
                    >
                      {activatedOpps.every(({ opp }) => expandedAngleClusters.has(opp.title))
                        ? "Collapse all"
                        : "Expand all"}
                    </Button>
                  </div>
                )}
                {activatedOpps.length > 0 && activatedOpps.map(({ opp, angles: oppAngles }) => {
                  const isExpanded = expandedAngleClusters.has(opp.title);
                  const slug = opp.title.replace(/\s+/g, '-').toLowerCase();
                  const tooltipBody = opp.why_promising || opp.core_tension;
                  return (
                    <div key={opp.title} className="space-y-2">
                      <div className="flex items-center justify-between px-1">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => toggleAngleCluster(opp.title)}
                                className="flex items-center gap-2 text-left rounded-md hover-elevate active-elevate-2 px-1 py-0.5 -ml-1"
                                data-testid={`button-toggle-angle-cluster-${slug}`}
                                aria-expanded={isExpanded}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                )}
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                  <Target className="h-3.5 w-3.5" />
                                  {opp.title}
                                  <Badge variant="secondary" className="text-[10px] rounded-full px-2">{oppAngles.length}</Badge>
                                </h3>
                              </button>
                            </TooltipTrigger>
                            {tooltipBody && (
                              <TooltipContent side="bottom" align="start" className="max-w-md text-[12px] leading-relaxed">
                                <p className="font-semibold text-[11px] uppercase tracking-wider mb-1 opacity-70">{opp.title}</p>
                                <p>{tooltipBody}</p>
                                {opp.core_tension && opp.why_promising && opp.core_tension !== opp.why_promising && (
                                  <p className="mt-2 opacity-80">{opp.core_tension}</p>
                                )}
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[10px] h-6 rounded-full text-muted-foreground/60 hover:text-primary"
                          onClick={() => runSingleOpportunity(opp)}
                          disabled={isOppRunning(opp) || generateAnglesMutation.isPending}
                          data-testid={`button-generate-more-${slug}`}
                        >
                          {isOppRunning(opp) ? (
                            <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Generating...</>
                          ) : (
                            <><Plus className="h-3 w-3 mr-1" /> Generate More</>
                          )}
                        </Button>
                      </div>
                      {isExpanded && (
                        <AngleTable angles={sortAnglesByScore(oppAngles)} brandId={brandId!} />
                      )}
                    </div>
                  );
                })}
                {unmatchedAngles.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2 px-1">
                      <Sparkles className="h-3.5 w-3.5" />
                      {activatedOpps.length > 0 ? "Additional Angles" : "Narrative Angles"}
                      <Badge variant="secondary" className="text-[10px] rounded-full px-2">{unmatchedAngles.length}</Badge>
                    </h3>
                    <AngleTable angles={sortAnglesByScore(unmatchedAngles)} brandId={brandId!} />
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}

      {generation.status === "completed" && !analyzerOutput && (
        <div className="rounded-xl border border-dashed p-8 text-center fade-in-up">
          <p className="text-sm text-muted-foreground">No analyzer output available for this generation.</p>
        </div>
      )}

      {relatedGenerations.length > 0 && (
        <div className="fade-in-up-delay-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            Other Analyses for This Pair
          </h2>
          <div className="space-y-2">
            {relatedGenerations.map(g => (
              <Link key={g.id} href={`/brands/${brandId}/narrative-generations/${g.id}`}>
                <Card className="cursor-pointer hover:bg-muted/30 transition-colors" data-testid={`card-related-gen-${g.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <GenerationStatusBadge status={g.status} />
                    {g.angleCount && g.angleCount > 0 && (
                      <span className="text-xs text-muted-foreground">{g.angleCount} opportunities</span>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {new Date(g.createdAt!).toLocaleDateString()}
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Dialog open={showAddOppDialog} onOpenChange={(open) => { if (!addOpportunityMutation.isPending) setShowAddOppDialog(open); }}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-add-opportunity">
          <DialogHeader>
            <DialogTitle>Add Your Own Opportunity</DialogTitle>
            <DialogDescription>
              Describe the narrative opportunity you have in mind. We'll use the persona and product data to generate a fully structured opportunity for you.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              placeholder="e.g., There's a tension between wanting simple ingredients but feeling like simple means less effective. This persona has been conditioned to believe complex formulas are better..."
              value={oppUserInput}
              onChange={e => setOppUserInput(e.target.value)}
              className="min-h-[120px] text-sm"
              disabled={addOpportunityMutation.isPending}
              data-testid="input-opp-user-idea"
            />
            <p className="text-[11px] text-muted-foreground mt-2">
              The persona and product context will be sent automatically — just describe your idea.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowAddOppDialog(false)}
              disabled={addOpportunityMutation.isPending}
              data-testid="button-cancel-add-opp"
            >
              Cancel
            </Button>
            <Button
              onClick={() => addOpportunityMutation.mutate(oppUserInput)}
              disabled={!oppUserInput.trim() || addOpportunityMutation.isPending}
              data-testid="button-submit-add-opp"
            >
              {addOpportunityMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate Opportunity
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
